import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ESRI } from '@rsos/maps/constants/mapProviders';
import { fetchGISServices } from '@rsos/reduxDataStore';
import { selectMapProvider } from '@rsos/sinatra';
import checkGISServicesAccess from '@rsos/utils/checkGISServicesAccess';

const TWENTY_FOUR_HOURS = 86400;

/**
 * A GIS token hook if the agency has GIS services capability enabled
 */
const useGISToken = () => {
  const dispatch = useDispatch();

  const orgName = useSelector(state => state.sinatra.user.currentOrg.name);
  const mapProvider = useSelector(selectMapProvider);
  const prevMapProviderRef = useRef(mapProvider);

  const hasGISServicesAccess = useSelector(state =>
    checkGISServicesAccess({
      psaps: state.psaps,
      sinatra: state.sinatra,
    }),
  );

  const gisIdentities = useSelector(state => state.gisServices.gisIdentities);

  // GIS token expiration value is the same throughout the GIS identities array
  const tokenExpiresIn =
    gisIdentities?.[0]?.tokenExpiresIn || Date.now() + TWENTY_FOUR_HOURS;

  // Fetch GIS services on load
  useEffect(() => {
    if (hasGISServicesAccess) {
      dispatch(fetchGISServices({ orgName }));
    }
  }, [dispatch, hasGISServicesAccess, orgName]);

  // Refetch GIS services when switching back to ESRI from another provider
  // This creates fresh ArcGIS layer objects (the old ones are stale after map was destroyed)
  // Note: Redux active state is preserved since we don't call clearAllGISServices
  useEffect(() => {
    const prevProvider = prevMapProviderRef.current;
    prevMapProviderRef.current = mapProvider;

    // Only refetch when switching TO ESRI from a different provider
    if (
      hasGISServicesAccess &&
      mapProvider === ESRI &&
      prevProvider &&
      prevProvider !== ESRI
    ) {
      dispatch(fetchGISServices({ orgName }));
    }
  }, [dispatch, hasGISServicesAccess, mapProvider, orgName]);

  // Renew GIS token based on token expiration time
  useEffect(() => {
    const expirationThreshold = 1000 * 60 * 5;
    const expirationTime = tokenExpiresIn * 1000 - expirationThreshold || 0;
    const currentTime = new Date().getTime();
    const shouldRenew = expirationTime > 0 && expirationTime <= currentTime;

    if (hasGISServicesAccess && shouldRenew) {
      const renewGISToken = setTimeout(() => {
        dispatch(fetchGISServices({ orgName }));
      }, expirationTime);

      return () => clearTimeout(renewGISToken);
    }
  }, [dispatch, hasGISServicesAccess, orgName, tokenExpiresIn]);
};

export default useGISToken;
